import React, { useEffect, useState } from "react";
import Layout from "../../../components/Layout";
import { Link } from "react-router-dom";
import { DownloadIcon, EditIcon, LockOpenIcon } from "../../../assets/icons/Icons";
import Chip from "../../../components/Chip";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import SearchFilter from "../../../components/filters/SearchFilter";
import EditUser from "../../../components/user/Edit";
import CreateUserForm from "../../../components/user/Create";
import MessagePopup from "../../../components/MessagePopup";
import ConfirmModal from "../../../components/ConfirmModal";
import DataTable from "../../../components/DataTable";
import ResendInviteLink from "../../../components/form/ResendInviteLink";
import { unlockUser } from "../../../api/users/unlockUser";
import useData from "../../../hooks/useData";
import useExportCSV from "../../../hooks/useExportCSV";
import { GetUserStatuses } from "../../../api/users/getUserStatuses";
import FilterUsers from "../../../api/users/filterUsers";
import RadioFilter from "../../../components/filters/RadioFilter";
import DropdownButton from "../../../components/DropdownButton";
import ResetFilters from "../../../components/filters/ResetFilters";
import { useTranslation } from "react-i18next";
import SelectFilter from "../../../components/filters/SelectFilter";
import CustomTooltip from "../../../components/CustomTooltip";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import { useUserContext } from "../../../contexts/UserContext";
import { useThemeContext } from "../../../contexts/ThemeContext";
import { RBAC_RESOURCE_USERS, RBAC_RESOURCE_USERS_UNLOCK } from "../../../contstants";

const UNLOCK_ROLES = ["admin"];

const Index = () => {
  const { t } = useTranslation();

  const {
    data,
    refreshData,
    errorMessage,
    loadingData,
    pageCount,
    newItemAdded,
    setNewItemAdded,
    itemUpdated,
    setItemUpdated,
    selectedItem,
    setSelectedItem,
    showEditItem,
    setShowEditItem,
    showCreateItem,
    setShowCreateItem,
  } = useData(FilterUsers);

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();
  const { userData, hasPermission, hasDynamicPermissions } = useUserContext();
  const { subdomain } = useThemeContext();

  const isOxford = subdomain?.startsWith('oxfordgiftcard') || subdomain?.startsWith('oxf-bpssnp');
  const isRestrictedRole = ['client_admin', 'office_admin', 'agent'].includes(userData?.role);
  // Gate on the dynamic permission when the user carries one; otherwise
  // preserve the existing static-role behavior (unchanged for non-migrated
  // users).
  const canAddUser = hasDynamicPermissions()
    ? hasPermission(RBAC_RESOURCE_USERS, "write")
    : !(isOxford && isRestrictedRole);
  // When the user carries a dynamic permission map, unlock is gated on the
  // users_unlock.write grant (revocable per role, no deploy); otherwise the
  // static role behavior is preserved.
  const canUnlockUser = hasDynamicPermissions()
    ? hasPermission(RBAC_RESOURCE_USERS_UNLOCK, "write")
    : isOxford && UNLOCK_ROLES.includes(userData?.role);

  const [selectedForUnlock, setSelectedForUnlock] = useState(null);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockErrorMessage, setUnlockErrorMessage] = useState(null);
  const [unlockedUser, setUnlockedUser] = useState(null);

  useEffect(() => {
    setPageTitle(t("Settings"));
    setBreadcrumbs([t("Settings"), t("Users")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  const { handleExportCSV } = useExportCSV(FilterUsers, "users");

  const handleEditClick = (e, item) => {
    e.preventDefault();
    setSelectedItem(item);
    setShowEditItem(true);
  };

  const handleUnlockClick = (e, item) => {
    e.preventDefault();
    setUnlockErrorMessage(null);
    setSelectedForUnlock(item);
  };

  const handleUnlockConfirm = async () => {
    try {
      setUnlockLoading(true);
      setUnlockErrorMessage(null);
      await unlockUser(selectedForUnlock.id);
      setUnlockLoading(false);
      setUnlockedUser(selectedForUnlock);
      setSelectedForUnlock(null);
      refreshData();
    } catch (err) {
      setUnlockLoading(false);
      setUnlockErrorMessage(err.message);
    }
  };

  return (
    <Layout title={t("Settings")} breadcrumbs={t("Users")}>
      {showCreateItem && (
        <CreateUserForm
          onCreate={() => {
            setShowCreateItem(false);
            setNewItemAdded(true);
          }}
          onClose={() => setShowCreateItem(false)}
        />
      )}
      {newItemAdded && (
        <MessagePopup
          title={t("User Added!")}
          message={t("The user has been successfully created!")}
          onClose={() => setNewItemAdded(false)}
        />
      )}
      {showEditItem && (
        <EditUser
          user={selectedItem}
          onClose={() => setShowEditItem(false)}
          onUpdate={() => {
            setShowEditItem(false);
            setItemUpdated(true);
          }}
        />
      )}
      {itemUpdated && (
        <MessagePopup
          title={t("User Updated!")}
          message={t("The user has been successfully updated!")}
          onClose={() => setItemUpdated(false)}
        />
      )}
      {selectedForUnlock && (
        <ConfirmModal
          title={t("Unlock User")}
          question={t("Are you sure you want to unlock this user?")}
          confirmButtonText={t("Unlock")}
          loadingText={t("Unlocking...")}
          deleteLoading={unlockLoading}
          deleteErrorMessage={unlockErrorMessage}
          onConfirm={handleUnlockConfirm}
          onClose={() => setSelectedForUnlock(null)}
        />
      )}
      {unlockedUser && (
        <MessagePopup
          title={t("User Unlocked!")}
          message={`${unlockedUser.first_name} ${unlockedUser.last_name} ${t("unlocked")}`}
          onClose={() => setUnlockedUser(null)}
        />
      )}

      <DataTable
        filtersTop={
          <>
            <FilterContextProvider name="search_in_fields">
              <SelectFilter
                options={[
                  {
                    value: "first_name",
                    label: t("First Name"),
                  },
                  {
                    value: "last_name",
                    label: t("Last Name"),
                  },
                  {
                    value: "email",
                    label: t("Email"),
                  },
                  {
                    value: "office",
                    label: t("Office"),
                  },
                ]}
                defaultValue="first_name"
                placeholder={<div>{t("Search in fields")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider name="s">
              <SearchFilter placeholderText={t("Search")} />
            </FilterContextProvider>
          </>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="status">
              <SelectFilter
                options={[
                  {
                    value: "active",
                    label: t("Active"),
                  },
                  {
                    value: "deleted",
                    label: t("Deleted"),
                  },
                ]}
                placeholder={<div>{t("Status")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={
          <>
            <button
              onClick={handleExportCSV}
              className="btn btn-secondary btn-medium"
            >
              <DownloadIcon />
              {t("Export CSV")}
            </button>
            {canAddUser && (
              <button
                onClick={() => setShowCreateItem(true)}
                className="btn btn-primary btn-medium"
              >
                {t("Add")}
              </button>
            )}
          </>
        }
        columns={[
          { header: t("User ID"), accessor: "id" },
          { header: t("First Name"), accessor: "first_name" },
          { header: t("Last Name"), accessor: "last_name" },
          { header: t("Email"), accessor: "email" },
          { header: t("Role"), accessor: "role_display" },
          { header: t("Status"), accessor: "latest_status" },
          { header: t("Action"), accessor: "actions" },
        ]}
        rows={(data.data || []).map((u) => ({
          ...u,
          // Display the actual (custom) role name; `role` keeps the
          // enforcement value and stays untouched on the row.
          role_display: u.role_name || u.role,
        }))}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        actions={(item) => (
          <>
            {canUnlockUser && item.latest_status === "locked_out" && (
              <Link
                to="#"
                onClick={(e) => handleUnlockClick(e, item)}
                style={{ color: "var(--color-warning)" }}
              >
                <CustomTooltip icon={<LockOpenIcon />} message={t("Unlock")} />
              </Link>
            )}
            <Link to="#" onClick={(e) => handleEditClick(e, item)}>
              <CustomTooltip icon={<EditIcon />} message={t("Edit")} />
            </Link>
            {!item.confirmed && (
              <ResendInviteLink user_id={item.id} showAsTooltip />
            )}
          </>
        )}
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "latest_status":
              if (value === "locked_out") {
                return <Chip state="pending" label={t("Locked")} showAsTooltop />;
              }
              return (
                <Chip
                  state={value === "deleted" ? "inactive" : value}
                  label={
                    value === "deleted"
                      ? "Inactive"
                      : value.charAt(0).toUpperCase() + value.slice(1)
                  }
                  showAsTooltop
                />
              );
            case "role_display":
              return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
};

export default Index;
